<template>
  <div class="mini-tab-content">
    <div
      v-if="loadingIntercurrences"
      class="interc-loading"
    >
      <b-spinner
        small
        variant="warning"
      />
      <span>{{ $t('involvedPanel.intercurrences.toolbar.loading') }}</span>
    </div>
    <template v-else>
      <div class="interc-toolbar">
        <span class="interc-toolbar__title">
          <feather-icon
            icon="AlertTriangleIcon"
            size="13"
            class="mr-25 text-warning"
          />
          {{ $tc('involvedPanel.intercurrences.toolbar.countIntercurrences', intercurrences.length) }}
        </span>
        <button
          class="btn btn-sm btn-warning"
          @click="showIntercurrenceModal = true"
        >
          <feather-icon
            icon="PlusIcon"
            size="13"
            class="mr-25"
          />
          {{ $t('involvedPanel.intercurrences.toolbar.registerButton') }}
        </button>
      </div>

      <div
        v-if="!intercurrences.length"
        class="interc-empty"
      >
        <feather-icon
          icon="ShieldOffIcon"
          size="26"
        />
        <p>{{ $t('involvedPanel.intercurrences.empty.noRecorded') }}</p>
      </div>

      <div
        v-if="intercurrences.length"
        class="interc-list"
      >
        <div
          v-for="ic in intercurrences"
          :key="ic.id"
          :class="['interc-item', { 'interc-item--cancelled': ic.cancelled_by_user_id }]"
        >
          <div class="interc-item__top">
            <div class="interc-item__badge">
              <feather-icon
                :icon="ic.cancelled_by_user_id ? 'XCircleIcon' : 'AlertTriangleIcon'"
                size="11"
                class="mr-25"
              />
              {{ ic.intercurrence ? ic.intercurrence.name : '—' }}
            </div>
            <button
              v-if="!ic.cancelled_by_user_id"
              class="interc-item__del"
              :title="$t('involvedPanel.intercurrences.item.deleteButton.title')"
              @click="deleteIntercurrence(ic.id)"
            >
              <feather-icon
                icon="Trash2Icon"
                size="13"
              />
            </button>
          </div>
          <p class="interc-item__desc">
            {{ ic.description }}
          </p>
          <div class="interc-item__meta">
            <feather-icon
              icon="UserIcon"
              size="11"
            />
            <span>{{ ic.user ? ic.user.name : '—' }}</span>
            <span class="interc-item__dot" />
            <feather-icon
              icon="ClockIcon"
              size="11"
            />
            <span>{{ ic.created_at | formatDate }}</span>
          </div>
          <div
            v-if="ic.cancelled_by_user_id"
            class="interc-item__cancelled-row"
          >
            <feather-icon
              icon="XCircleIcon"
              size="11"
              class="mr-25"
            />
            {{ $t('involvedPanel.intercurrences.item.cancelled.label') }}
            <strong>{{ ic.cancelled_by ? ic.cancelled_by.name : '—' }}</strong>
            · {{ ic.cancelled_at | formatDate }}
          </div>
        </div>
      </div>
    </template>

    <b-modal
      v-model="showIntercurrenceModal"
      :title="$t('involvedPanel.intercurrences.modal.title')"
      :title-class="'font-weight-bold'"
      size="md"
      centered
      no-close-on-backdrop
      @hidden="newIntercurrence = { intercurrence_id: null, description: '' }"
    >
      <div class="form-group mb-2">
        <label class="interc-label"><span class="required">*</span> {{ $t('involvedPanel.intercurrences.modal.typeLabel') }}</label>
        <v-select
          v-model="newIntercurrence.intercurrence_id"
          :options="intercurrenceCatalog"
          :reduce="(i) => i.id"
          label="name"
          :placeholder="$t('involvedPanel.intercurrences.modal.typePlaceholder')"
          class="vselect"
          :loading="loadingCatalog"
        />
      </div>
      <div class="form-group mb-1">
        <label class="interc-label"><span class="required">*</span> {{ $t('involvedPanel.intercurrences.modal.descriptionLabel') }}</label>
        <textarea
          v-model="newIntercurrence.description"
          class="form-control"
          rows="4"
          :placeholder="$t('involvedPanel.intercurrences.modal.descriptionPlaceholder')"
          maxlength="2000"
        />
        <div class="interc-char-count">
          {{ (newIntercurrence.description || '').length }}/2000
        </div>
      </div>

      <template #modal-footer>
        <button
          class="btn btn-sm btn-outline-secondary mr-1"
          @click="showIntercurrenceModal = false"
        >
          {{ $t('involvedPanel.intercurrences.modal.cancelButton') }}
        </button>
        <button
          class="btn btn-sm btn-warning"
          :disabled="savingIntercurrence || !newIntercurrence.intercurrence_id || !newIntercurrence.description"
          @click="saveIntercurrence"
        >
          <b-spinner
            v-if="savingIntercurrence"
            small
            class="mr-25"
          />
          <feather-icon
            v-else
            icon="AlertTriangleIcon"
            size="13"
            class="mr-25"
          />
          {{ $t('involvedPanel.intercurrences.modal.registerButton') }}
        </button>
      </template>
    </b-modal>
  </div>
</template>

<script>
import involvedPanelMixin from './involvedPanelMixin'

export default {
  name: 'IntercurrencesPanel',
  mixins: [involvedPanelMixin],
  data() {
    return {
      intercurrences: [],
      loadingIntercurrences: false,
      intercurrenceCatalog: [],
      loadingCatalog: false,
      savingIntercurrence: false,
      newIntercurrence: { intercurrence_id: null, description: '' },
      showIntercurrenceModal: false,
    }
  },
  mounted() {
    this.loadIntercurrences()
  },
  methods: {
    async loadIntercurrences() {
      this.loadingIntercurrences = true
      this.loadingCatalog = true
      try {
        const [listRes, catalogRes] = await Promise.all([
          this.$http.get(`${this.$store.state.api}occurrence_intercurrences`, {
            params: { occurrence_id: this.occurrenceId, involved_id: this.involvedId },
          }),
          this.$http.get(`${this.$store.state.api}intercurrences/for-occurrence/${this.occurrenceId}`),
        ])
        this.intercurrences = listRes.data.data || listRes.data || []
        this.intercurrenceCatalog = catalogRes.data.data || catalogRes.data || []
      } catch (e) {
        this.intercurrences = []
      } finally {
        this.loadingIntercurrences = false
        this.loadingCatalog = false
      }
    },
    async saveIntercurrence() {
      this.savingIntercurrence = true
      try {
        const { user } = this.$store.state
        const payload = {
          occurrence_id: this.occurrenceId,
          involved_id: this.involvedId,
          intercurrence_id: this.newIntercurrence.intercurrence_id,
          description: this.newIntercurrence.description,
          user_id: user.id,
        }
        const res = await this.$http.post(`${this.$store.state.api}occurrence_intercurrences`, payload)
        const saved = res.data.data || res.data
        this.intercurrences.push(saved)
        this.newIntercurrence = { intercurrence_id: null, description: '' }
        this.showIntercurrenceModal = false
        this.$message(
          this.$t('involvedPanel.intercurrences.messages.success.title'),
          this.$t('involvedPanel.intercurrences.messages.success.body'),
          'success',
        )
      } catch (e) {
        this.$message(
          this.$t('involvedPanel.intercurrences.messages.error.title'),
          this.$t('involvedPanel.intercurrences.messages.error.registerBody'),
          'error',
        )
      } finally {
        this.savingIntercurrence = false
      }
    },
    async deleteIntercurrence(id) {
      const confirmed = await this.$bvModal.msgBoxConfirm(
        this.$t('involvedPanel.intercurrences.confirm.deleteMessage'),
        {
          title: this.$t('involvedPanel.intercurrences.confirm.deleteTitle'),
          okTitle: this.$t('involvedPanel.intercurrences.confirm.deleteOkButton'),
          cancelTitle: this.$t('involvedPanel.intercurrences.confirm.deleteCancelButton'),
          okVariant: 'danger',
          centered: true,
          size: 'sm',
        },
      )
      if (!confirmed) return
      try {
        const res = await this.$http.delete(`${this.$store.state.api}occurrence_intercurrences/${id}`)
        const updated = res.data.data || res.data
        const idx = this.intercurrences.findIndex(i => i.id === id)
        if (idx !== -1) this.$set(this.intercurrences, idx, updated)
      } catch (e) {
        this.$message(
          this.$t('involvedPanel.intercurrences.messages.error.title'),
          this.$t('involvedPanel.intercurrences.messages.error.deleteBody'),
          'error',
        )
      }
    },
  },
}
</script>
